import type { ApiClientErrorCode, DecorKind, DecorListItem } from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { DecorFilterChips, type DecorFilterField } from './DecorFilterChips';
import { DecorResultCard } from './DecorResultCard';

/**
 * Presenter Экрана 2.2.6 (список каталога декора). См. ADR-0005.
 *
 * Чистая функция от props к JSX. Никаких useEffect / fetch / useNavigate.
 * 5 состояний (P-021): idle / loading / success / empty / error.
 *
 * VideoButton тут НЕ ставим: он уже на Block 2 KindPicker (Экран 2.2.1, Phase 5).
 * Catalog — дочерний экран. Дублирование запрещено по CLAUDE.md §16.4.
 * Аудит сквозного placement'а — B-023 в BACKLOG.
 */
export type DecorsCatalogState = 'idle' | 'loading' | 'success' | 'empty' | 'error';

export interface DecorsCatalogViewProps {
  kind: DecorKind;
  state: DecorsCatalogState;
  /** Только при state='success' / 'empty' */
  items?: readonly DecorListItem[];
  /** Cursor следующей страницы. null → нет данных или достигнут конец. */
  nextCursor: string | null;
  /** Только при state='error' */
  error?: { code: ApiClientErrorCode; message: string };

  /** Фото-2 (PR-DATA4): signed-URL по `primaryPhotoKey`. Контейнер передаёт из
   *  useDecorImageUrls; нет ключа/url → плейсхолдер в карточке. */
  imageUrls?: Record<string, string>;

  // Опции фасета ИЗ ДАННЫХ (ADR-0054) — Бренд/Тип сужены выбранной подкатегорией.
  subcategoryOptions: readonly string[];
  brandOptions: readonly string[];
  typeOptions: readonly string[];
  /** Опции фасета грузятся — пустые группы покажут «Загружаем…». */
  facetsLoading?: boolean;

  // Текущий выбор chip'ов (preview, до клика «Найти»)
  selectedSubcategories: readonly string[];
  selectedBrands: readonly string[];
  selectedTypes: readonly string[];

  onToggle: (field: DecorFilterField, value: string) => void;
  onResetGroup: (field: DecorFilterField) => void;
  onSearch: () => void;
  onResetAll: () => void;
  onLoadMore: () => void;
  onItemClick: (globalId: string) => void;
}

// Корпус ЛДСП в KindPicker остаётся «Корпус (ЛДСП)» (так в PRD §2.2.1), но
// для catalog header выбрана форма без скобок — заголовок экрана читается чище
// и встаёт в один ряд с «Фасады» / «Столешницы». См. ТЗ Phase 6 уточнение #1.
function titleForKind(kind: DecorKind): string {
  switch (kind) {
    case 'facade':
      return 'Фасады';
    case 'countertop':
      return 'Столешницы';
    case 'panel':
      return 'Корпус ЛДСП';
  }
}

export function DecorsCatalogView(props: DecorsCatalogViewProps) {
  const title = titleForKind(props.kind);
  const titleLower = title.toLowerCase();

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title={title} />

      {/* Filter panel — всегда видна. */}
      <section className="border-b border-icon-border p-6 flex flex-col gap-6">
        <DecorFilterChips
          subcategoryOptions={props.subcategoryOptions}
          brandOptions={props.brandOptions}
          typeOptions={props.typeOptions}
          optionsLoading={props.facetsLoading}
          selectedSubcategories={props.selectedSubcategories}
          selectedBrands={props.selectedBrands}
          selectedTypes={props.selectedTypes}
          onToggle={props.onToggle}
          onResetGroup={props.onResetGroup}
        />
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={props.onSearch}
            aria-label={`Найти декоры (${titleLower}) по выбранным фильтрам`}
            className={[
              'w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink',
              'font-bold text-[18px] py-3',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
            ].join(' ')}
          >
            Найти
          </button>
          <button
            type="button"
            onClick={props.onResetAll}
            aria-label="Сбросить все фильтры"
            className={[
              'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
              'text-[16px] py-3',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
            ].join(' ')}
          >
            Сбросить фильтр
          </button>
        </div>
      </section>

      {/* Results area — switch on state */}
      <section className="flex-1 p-4 overflow-y-auto">
        <StateView {...props} />
      </section>

      <BottomBar />
    </main>
  );
}

function StateView(props: DecorsCatalogViewProps) {
  switch (props.state) {
    case 'idle':
      return <IdleState />;
    case 'loading':
      return <LoadingState />;
    case 'success':
      return (
        <div className="flex flex-col gap-5">
          <p className="text-muted text-base m-0">Найдено: {props.items?.length ?? 0}</p>
          <div className="flex flex-col gap-5">
            {(props.items ?? []).map((item) => (
              <DecorResultCard
                key={item.globalId}
                item={item}
                // Phase 6 retrofit: list endpoint возвращает dominantLab
                // (когда есть). B-022 SKU (Slotex без фото) → undefined →
                // серый dashed swatch — это правильный data-quality marker,
                // а не дефолт для всех 85 SKU.
                dominantLab={item.dominantLab}
                imageUrl={
                  item.primaryPhotoKey ? (props.imageUrls?.[item.primaryPhotoKey] ?? null) : null
                }
                onItemClick={props.onItemClick}
              />
            ))}
          </div>
          {props.nextCursor !== null && (
            <button
              type="button"
              onClick={props.onLoadMore}
              aria-label="Показать ещё"
              className={[
                'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
                'text-[16px] py-3',
                'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
              ].join(' ')}
            >
              Показать ещё
            </button>
          )}
        </div>
      );
    case 'empty':
      return <EmptyState onResetAll={props.onResetAll} />;
    case 'error':
      return <ErrorState message={props.error?.message ?? ''} />;
  }
}

function IdleState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-muted text-[18px] max-w-[280px] m-0 leading-normal">
        Выберите фильтры и нажмите «Найти»
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-muted text-[18px] m-0">Загружаем…</p>
    </div>
  );
}

function EmptyState({ onResetAll }: { onResetAll: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-muted text-[18px] max-w-[280px] m-0 leading-normal">
        По выбранным фильтрам ничего не нашли. Попробуйте сменить фильтры.
      </p>
      <button
        type="button"
        onClick={onResetAll}
        aria-label="Сбросить все фильтры"
        className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
      >
        Сбросить все фильтры
      </button>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-error text-[18px] max-w-[280px] m-0 leading-normal">
        Не удалось получить ответ. Проверьте интернет и попробуйте снова.
      </p>
      {message && <p className="text-muted text-sm">{message}</p>}
    </div>
  );
}
